import { apiGet, jsonFromResponse, withQuery } from "./client";
import {
  getCurrentUser,
  getWebDeviceId,
  isSignedIn,
  authFetch,
  authPost,
} from "@/shared/auth/session";

export type TrialStatus = {
  ok?: boolean;
  userId?: string | null;
  trialStart?: number | null;
  trialEnd?: number | null;
  status?: string | null;
  source?: string | null;
  error?: string;
  detail?: string;
};

export type TrialStartResponse = {
  ok?: boolean;
  userId?: string;
  trialStart?: number;
  trialEnd?: number;
  status?: string;
  source?: string;
};

export type SubscriptionEntitlement = {
  ok?: boolean;
  tier?: string | null;
  premium?: boolean;
  accountLinked?: boolean;
  multiDevice?: boolean;
  trialActive?: boolean;
  trialEndEpochMs?: number | null;
  hasPremiumAccess?: boolean;
};

export type AudioDailyLimits = {
  dailyArticleSummaryLimit?: number;
  dailyCategoryBriefingLimit?: number;
};

export type SubscriptionLedgerRow = {
  tier?: string | null;
  lifecycle?: string | null;
  status?: string | null;
  productId?: string | null;
  subscriptionId?: string | null;
  expiryEpochMs?: number | null;
  updatedAt?: string | null;
};

export type SubscriptionsResponse = {
  ok?: boolean;
  items?: SubscriptionLedgerRow[];
};

export async function fetchTrialStatus(): Promise<TrialStatus | null> {
  const userId = getCurrentUser()?.id || "";
  const path = withQuery("/api/trial", {
    deviceId: getWebDeviceId(),
    accountStableId: userId,
    effective: "1",
  });
  const res = await authFetch(path);
  return jsonFromResponse<TrialStatus | null>(res, { fallbackCode: "trial_failed" });
}

export function startWebTrial(): Promise<TrialStartResponse> {
  return authPost<TrialStartResponse>(
    "/api/trial/start-web",
    {
      source: "web_subscription_page",
    },
    { activitySource: "user_initiated" },
  );
}

export async function fetchSubscriptionEntitlement(): Promise<SubscriptionEntitlement> {
  if (!isSignedIn()) {
    // Account proof requires a session. Omit device access/trial fields so the
    // independently verified public config can still supply those entitlements.
    return { ok: true, tier: "FREE", premium: false, accountLinked: false, multiDevice: false };
  }
  const path = withQuery("/api/subscriptions/entitlement", {
    deviceId: getWebDeviceId(),
  });
  const res = await authFetch(path);
  return jsonFromResponse<SubscriptionEntitlement>(res, {
    fallbackCode: "entitlement_failed",
  });
}

export async function fetchSubscriptions(): Promise<SubscriptionsResponse> {
  const path = withQuery("/api/subscriptions", {
    deviceId: getWebDeviceId(),
  });
  const res = await authFetch(path);
  return jsonFromResponse<SubscriptionsResponse>(res, {
    fallbackCode: "subscriptions_failed",
  });
}

export function fetchAudioDailyLimits(): Promise<AudioDailyLimits> {
  return apiGet<AudioDailyLimits>("/api/limits/audio-daily", undefined, {
    fallbackCode: "audio_limits_failed",
  });
}

export function isTrialActive(trial: TrialStatus | null | undefined, now = Date.now()): boolean {
  const status = String(trial?.status || "").toUpperCase();
  const end = Number(trial?.trialEnd);
  return status === "ACTIVE" && Number.isFinite(end) && end > now;
}

export function trialDaysRemaining(epochMs: number | null | undefined, now = Date.now()): number {
  const end = Number(epochMs);
  if (!Number.isFinite(end) || end <= now) return 0;
  return Math.max(1, Math.ceil((end - now) / 86_400_000));
}
